import { z } from 'zod';

export const storageLocationTypeSchema = z.enum([
  'freezer',
  'cooler',
  'dry-storage',
  'bar-storage',
  'pantry',
]);

export type StorageLocationType = z.infer<typeof storageLocationTypeSchema>;

export const temperatureStatusSchema = z.enum([
  'normal',
  'warning',
  'critical',
]);

export type TemperatureStatus = z.infer<typeof temperatureStatusSchema>;

export const temperatureReadingSchema = z.object({
  timestamp: z.string(),
  value: z.number(),
  unit: z.enum(['C', 'F']),
});

export type TemperatureReading = z.infer<typeof temperatureReadingSchema>;

export const temperatureThresholdsSchema = z.object({
  minCritical: z.number(),
  minWarning: z.number(),
  maxWarning: z.number(),
  maxCritical: z.number(),
  unit: z.enum(['C', 'F']),
});

export type TemperatureThresholds = z.infer<typeof temperatureThresholdsSchema>;

export const alertConfigSchema = z.object({
  enabled: z.boolean(),
  emailNotifications: z.boolean(),
  smsNotifications: z.boolean(),
  emailRecipients: z.array(z.string()),
  smsRecipients: z.array(z.string()),
  escalationDelayMinutes: z.number(),
});

export type AlertConfig = z.infer<typeof alertConfigSchema>;

export const storageLocationWithTempSchema = z.object({
  locationId: z.string(),
  name: z.string(),
  type: storageLocationTypeSchema,
  isActive: z.boolean(),
  sensorId: z.string().nullable(),
  currentTemperature: temperatureReadingSchema.nullable(),
  temperatureStatus: temperatureStatusSchema,
  thresholds: temperatureThresholdsSchema,
  alertConfig: alertConfigSchema,
  itemCount: z.number(),
  lastUpdated: z.string(),
});

export type StorageLocationWithTemp = z.infer<typeof storageLocationWithTempSchema>;

export const storageLocationsResponseSchema = z.object({
  data: z.array(storageLocationWithTempSchema),
  meta: z.object({ siteId: z.string() }),
});

export type StorageLocationsResponse = z.infer<typeof storageLocationsResponseSchema>;

export const temperatureHistorySchema = z.object({
  locationId: z.string(),
  readings: z.array(temperatureReadingSchema),
  alerts: z.array(
    z.object({
      id: z.string(),
      timestamp: z.string(),
      type: z.enum(['warning', 'critical']),
      temperature: z.number(),
      message: z.string(),
      acknowledged: z.boolean(),
      acknowledgedBy: z.string().nullable(),
      acknowledgedAt: z.string().nullable(),
    })
  ),
});

export type TemperatureHistory = z.infer<typeof temperatureHistorySchema>;
export type TemperatureAlert = TemperatureHistory['alerts'][number];

export function getTemperatureStatus(
  temp: number,
  thresholds: TemperatureThresholds
): TemperatureStatus {
  if (temp <= thresholds.minCritical || temp >= thresholds.maxCritical) {
    return 'critical';
  }
  if (temp <= thresholds.minWarning || temp >= thresholds.maxWarning) {
    return 'warning';
  }
  return 'normal';
}

export const DEFAULT_THRESHOLDS: Record<StorageLocationType, TemperatureThresholds> = {
  freezer: {
    minCritical: -25,
    minWarning: -22,
    maxWarning: -15,
    maxCritical: -10,
    unit: 'C',
  },
  cooler: {
    minCritical: 0,
    minWarning: 2,
    maxWarning: 6,
    maxCritical: 8,
    unit: 'C',
  },
  'dry-storage': {
    minCritical: 10,
    minWarning: 15,
    maxWarning: 25,
    maxCritical: 30,
    unit: 'C',
  },
  'bar-storage': {
    minCritical: 0,
    minWarning: 2,
    maxWarning: 6,
    maxCritical: 8,
    unit: 'C',
  },
  pantry: {
    minCritical: 10,
    minWarning: 15,
    maxWarning: 25,
    maxCritical: 30,
    unit: 'C',
  },
};
